import find from 'lodash/find';
import map from 'lodash/map';
import pick from 'lodash/pick';
import moment from 'moment-timezone';
import { encodeReduxFieldName } from '@xmatters/common/utils/string';
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from 'utils/permissions';
import permissionConstants from 'xm-constants/permissions';

const {
  VIEW_SCREEN_WEB_LOGIN,
  VIEW_SCREEN_PHONE_LOGIN,
  VIEW_SCREEN_MY_API_KEYS,
  VIEW_SCREEN_USER_API_KEYS,
  VIEW_SCREEN_USER_ROLES,
  VIEW_SCREEN_MY_ROLES,
  ABILITY_ACT_SUPPORTUSER,
  ABILITY_ACT_EDIT_MY_DETAILS,
  VIEW_MENU_MY_PROFILE,
  ABILITY_OVERRIDE_EXTERNALDATALOCK,
  ABILITY_ACT_MANAGE_STAKEHOLDER_USERS,
  ABILITY_ACT_MANAGE_EXTERNALLY_OWNED,
  ABILITY_EDIT_MY_DEVICES,
  VIEW_SCREEN_ADD_DEVICE,
  ABILITY_ACT_ACCESS_PRIVILEGED_DEVICES,
  ABILITY_ACT_UNLOCK_ACCOUNT,
} = permissionConstants;

export const canViewSchedule = (permissions, loggedInUserId) => userIdToGet =>
  userIdToGet === loggedInUserId
    ? hasAnyPermission(
        permissions,
        permissionConstants.VIEW_SCREEN_MY_SCHEDULES,
        permissionConstants.VIEW_SCREEN_TEMPORARY_REPLACEMENT,
      )
    : hasAnyPermission(
        permissions,
        permissionConstants.VIEW_SCREEN_USER_SCHEDULE,
        permissionConstants.VIEW_SCREEN_TEMPORARY_REPLACEMENT,
      );

export const canViewDevices = (permissions, loggedInUserId) => userIdToGet =>
  userIdToGet === loggedInUserId
    ? hasPermission(permissions, permissionConstants.VIEW_SCREEN_MY_DEVICES)
    : hasPermission(permissions, permissionConstants.VIEW_SCREEN_USER_DEVICES);

export const canViewSubscriptions =
  (permissions, loggedInUserId) => userIdToGet =>
    userIdToGet === loggedInUserId
      ? hasPermission(
          permissions,
          permissionConstants.VIEW_MENUITEM_MY_SUBSCRIPTIONS,
        )
      : false;

export const canViewSupervisedUsers = permissions =>
  hasPermission(permissions, permissionConstants.VIEW_SCREEN_USERS);

export const canEditSupervisors = permissions =>
  hasPermission(
    permissions,
    permissionConstants.VIEW_SCREEN_EDIT_PERSON_SUPERVISORS,
  );

export const canSuperviseUsers = permissions =>
  hasPermission(permissions, permissionConstants.ABILITY_ACT_PERSON_SUPERVISOR);

export const loggedInUserIsSupervisor = (loggedInUserId, userToGet) =>
  map(userToGet?.supervisors?.data, 'id').includes(loggedInUserId);

export const hasRoleOnRole = (targetUser, loggedInUserProfile, roleName) => {
  const roleUuids = map(targetUser?.roles?.data, 'id');
  const rolePermissions = loggedInUserProfile?.rolePermissions?.data;
  return roleUuids.some(roleUuid =>
    find(rolePermissions, { id: roleUuid })?.roleOnRolePermissions?.includes(
      roleName,
    ),
  );
};

export const hasEditRoleOnRole = (targetUser, loggedInUserProfile) =>
  hasRoleOnRole(targetUser, loggedInUserProfile, 'EDIT_PERSON');

export const hasUseRoleOnRole = (targetUser, loggedInUserProfile) =>
  hasRoleOnRole(targetUser, loggedInUserProfile, 'USE_PERSON');

export const canViewSupervisors = (permissions, loggedInUser) => userToGet =>
  userToGet?.id === loggedInUser?.id
    ? hasAllPermissions(
        permissions,
        permissionConstants.VIEW_SCREEN_VIEW_PERSON_SUPERVISORS,
        permissionConstants.ABILITY_ACT_EDIT_MY_DETAILS,
      )
    : (hasAllPermissions(
        permissions,
        permissionConstants.VIEW_SCREEN_VIEW_PERSON_SUPERVISORS,
      ) &&
        loggedInUserIsSupervisor(loggedInUser?.id, userToGet)) ||
      hasEditRoleOnRole(userToGet, loggedInUser);

export const canViewGroups = permissions => () =>
  hasPermission(
    permissions,
    permissionConstants.VIEW_SCREEN_GROUPS_USER_BELONGS_TO,
  );

export const isTargetUserCompanyAdmin = targetUser =>
  targetUser?.roles?.data?.map(item => item.name).includes('Company Admin');

export const canLogInAsOtherUsers = (
  permissions,
  loggedInUser,
  isCompanyAdmin,
  isLoggedInUsersProfile,
  targetUser,
) => {
  if (isLoggedInUsersProfile) {
    return false;
  }

  return (
    (hasPermission(permissions, ABILITY_ACT_SUPPORTUSER) ||
      loggedInUser?.isSuperAdmin ||
      isCompanyAdmin) &&
    !isTargetUserCompanyAdmin(targetUser)
  );
};

export const canRevokeTokens = (
  permissions,
  loggedInUser,
  isLoggedInUsersProfile,
) => {
  if (isLoggedInUsersProfile) {
    return hasAllPermissions(
      permissions,
      VIEW_SCREEN_WEB_LOGIN,
      ABILITY_ACT_EDIT_MY_DETAILS,
    );
  }
  return hasPermission(permissions, VIEW_SCREEN_WEB_LOGIN);
};

export const canViewRoles = (permissions, isLoggedInUsersProfile) => {
  if (isLoggedInUsersProfile) {
    return hasPermission(permissions, VIEW_SCREEN_MY_ROLES);
  }
  return hasPermission(permissions, VIEW_SCREEN_USER_ROLES);
};

export const canViewApiKeys = (permissions, isLoggedInUsersProfile) => {
  if (isLoggedInUsersProfile) {
    return hasPermission(permissions, VIEW_SCREEN_MY_API_KEYS);
  }
  return hasPermission(permissions, VIEW_SCREEN_USER_API_KEYS);
};

export const canChangeLogin = permissions => {
  return hasAnyPermission(
    permissions,
    VIEW_SCREEN_WEB_LOGIN,
    VIEW_SCREEN_PHONE_LOGIN,
  );
};

export const canChangePassword = (
  permissions,
  loggedInUser,
  targetUser,
  isCompanyAdmin,
) => {
  const {
    loggedInUserProfile: { id: loggedInUserId },
  } = loggedInUser;

  if (loggedInUserId === targetUser?.id) {
    return (
      hasPermission(permissions, VIEW_SCREEN_WEB_LOGIN) &&
      (hasEditRoleOnRole(targetUser, loggedInUser?.loggedInUserProfile) ||
        hasPermission(permissions, ABILITY_ACT_EDIT_MY_DETAILS))
    );
  }

  return (
    hasPermission(permissions, VIEW_SCREEN_WEB_LOGIN) &&
    (isCompanyAdmin ||
      loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
      hasEditRoleOnRole(targetUser, loggedInUser?.loggedInUserProfile))
  );
};

export const canChangePhonePassword = (
  permissions,
  loggedInUser,
  targetUser,
  isCompanyAdmin,
) => {
  const {
    loggedInUserProfile: { id: loggedInUserId },
  } = loggedInUser;

  if (loggedInUserId === targetUser?.id) {
    return (
      hasPermission(permissions, VIEW_SCREEN_PHONE_LOGIN) ||
      hasEditRoleOnRole(targetUser, loggedInUser?.loggedInUserProfile)
    );
  }

  return (
    isCompanyAdmin ||
    loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
    hasEditRoleOnRole(targetUser, loggedInUser?.loggedInUserProfile)
  );
};

export const canShowMoreActionsButton = (
  permissions,
  isCompanyAdminOrSupervisor,
  loggedInUser,
  isLoggedInUsersProfile,
) => {
  return (
    canRevokeTokens(permissions, isLoggedInUsersProfile) ||
    canViewRoles(permissions, isLoggedInUsersProfile) ||
    canViewApiKeys(permissions, isLoggedInUsersProfile) ||
    canChangeLogin(permissions) ||
    hasPermission(permissions, ABILITY_ACT_SUPPORTUSER) ||
    isCompanyAdminOrSupervisor
  );
};

export const canEditApiKeys = (loggedInUser, targetUser, permissions) => {
  const loggedInUserProfile = loggedInUser?.loggedInUserProfile;
  const loggedInUserId = loggedInUserProfile?.id;

  if (loggedInUserId === targetUser?.id) {
    return hasPermission(permissions, ABILITY_ACT_EDIT_MY_DETAILS);
  }
  return (
    loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
    hasEditRoleOnRole(targetUser, loggedInUserProfile) ||
    loggedInUser.isCompanyAdmin
  );
};

export const canViewProfile = permissions => {
  return hasPermission(permissions, VIEW_MENU_MY_PROFILE);
};

export const getCanUnlockUser = (
  loggedInUserProfile,
  targetUser,
  isCompanyAdmin,
  permissions,
) => {
  if (isCompanyAdmin) {
    return true;
  }
  const loggedInUserId = loggedInUserProfile?.id;
  if (loggedInUserId === targetUser?.id) {
    return false;
  }

  if (!hasPermission(permissions, ABILITY_ACT_UNLOCK_ACCOUNT)) {
    return false;
  }

  return (
    loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
    hasEditRoleOnRole(targetUser, loggedInUserProfile)
  );
};

export const getMinutesUntilUnlock = (
  userLockedAtString,
  pwLockoutDuration,
  currentMoment,
) => {
  const unlockMomentTimeout = moment(userLockedAtString).add(
    pwLockoutDuration,
    'minutes',
  );
  return Math.max(
    0,
    Math.ceil(unlockMomentTimeout.diff(currentMoment, 'minutes')),
  );
};

export const canEditProfile = (
  loggedInUser,
  targetUser,
  isCompanyAdmin,
  permissions,
) => {
  const loggedInUserId = loggedInUser?.id;
  if (!canViewProfile(permissions)) {
    return false;
  }
  if (loggedInUserId === targetUser?.id) {
    return hasPermission(permissions, ABILITY_ACT_EDIT_MY_DETAILS);
  }
  return (
    isCompanyAdmin ||
    loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
    hasEditRoleOnRole(targetUser, loggedInUser)
  );
};

export const getCountryTargetUser = (siteIds, siteId) =>
  siteIds.filter(item => item.value === siteId);

export const getInitialOption = (options, value) =>
  options.filter(item => item.value === value)[0];

/**
 * Prefixing with `_` in case users make a property whose name is a number,
 * which redux-forms does not support: https://redux-form.com/8.3.0/docs/api/field.md/#props-you-can-pass-to-code-field-code-
 *    > Numeric field names, e.g. name="42" or name="foo.5.email",
 *    > are not supported, as they can be confused for array indexes.
 */
export const makeCustomPropertyName = name => `_${encodeReduxFieldName(name)}`;

export const setPropertyFieldsInitialValue = (properties = {}) => {
  const output = {};

  if (!properties) {
    return output;
  }
  Object.entries(properties).forEach(([key, value]) => {
    const encodedKey = makeCustomPropertyName(key);
    if (value !== null) {
      output[encodedKey] = Array.isArray(value)
        ? value.map(item => {
            return {
              value: item,
            };
          })
        : value;
    }
  });
  return output;
};

export const phonePasswordValidator = value => {
  if (!value) {
    return undefined;
  }
  return !!/^[0-9]{1,30}$/.test(value);
};

export const isFieldLocked = (
  lockedFields,
  name,
  isExternallyOwned,
  isCompanyAdmin,
  permissions,
) => {
  if (
    isCompanyAdmin ||
    hasPermission(permissions, ABILITY_OVERRIDE_EXTERNALDATALOCK) ||
    !isExternallyOwned
  ) {
    return false;
  }
  return lockedFields?.includes(name) || name === 'deviceName';
};

export const canEditUserStatus = (loggedInUser, targetUser, isCompanyAdmin) => {
  if (loggedInUser?.id === targetUser?.id) {
    return false;
  }
  if (
    isFieldLocked(
      targetUser?.locked,
      'status',
      targetUser.externallyOwned,
      isCompanyAdmin,
      loggedInUser?.permissions,
    )
  ) {
    return false;
  }
  return (
    isCompanyAdmin ||
    map(targetUser?.supervisors?.data, 'id')?.includes(loggedInUser?.id) ||
    hasEditRoleOnRole(targetUser, loggedInUser)
  );
};

export const canViewLicenseSection = (
  permissions,
  isStakeholderUsersEnabled,
  targetUser,
) => {
  return (
    hasPermission(permissions, ABILITY_ACT_MANAGE_STAKEHOLDER_USERS) &&
    isStakeholderUsersEnabled &&
    !isTargetUserCompanyAdmin(targetUser)
  );
};

export const minimizeUserPayload = payload =>
  pick(payload, [
    'id',
    'firstName',
    'language',
    'lastName',
    'recipientType',
    'targetName',
    'timezone',
    'webLogin',
  ]);

export const canEditDevices = (loggedInUser, targetUser, permissions) => {
  const loggedInUserProfile = loggedInUser?.loggedInUserProfile;
  const loggedInUserId = loggedInUserProfile?.id;

  if (loggedInUserId === targetUser?.id) {
    return hasAnyPermission(
      permissions,
      ABILITY_ACT_EDIT_MY_DETAILS,
      ABILITY_EDIT_MY_DEVICES,
    );
  }
  return (
    loggedInUserIsSupervisor(loggedInUserId, targetUser) ||
    hasEditRoleOnRole(targetUser, loggedInUserProfile) ||
    loggedInUser.isCompanyAdmin
  );
};

export const canAddDevices = (loggedInUser, targetUser, permissions) => {
  if (!hasPermission(permissions, VIEW_SCREEN_ADD_DEVICE)) {
    return false;
  }
  return canEditDevices(loggedInUser, targetUser, permissions);
};

export const getIsDeviceLocked = (
  isExternallyOwned,
  permissions,
  isCompanyAdmin,
) =>
  isCompanyAdmin ||
  hasPermission(permissions, ABILITY_ACT_MANAGE_EXTERNALLY_OWNED)
    ? false
    : isExternallyOwned;

export const getCanManagePrivileged = (permissions, isViewingOwnProfile) =>
  isViewingOwnProfile ||
  hasPermission(permissions, ABILITY_ACT_ACCESS_PRIVILEGED_DEVICES);

export const getCanSetCalenderSettings = (
  isWebOcrEnabled,
  isViewingOwnProfile,
  permission,
  userId,
  isCompanyAdmin,
) => {
  if (!isViewingOwnProfile || !isWebOcrEnabled) {
    return false;
  }
  return (
    isCompanyAdmin ||
    (canViewSchedule(permission, userId)(userId) &&
      canViewDevices(permission, userId)(userId))
  );
};
